import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box } from '@curves/ui-base';

import * as echarts from "echarts/core";
import { PieChart } from "echarts/charts";
import { TitleComponent, GridComponent, TooltipComponent, LegendComponent, DatasetComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { isEmpty } from "@Util/utils";
import { chartColorMap } from "@Local/Dashboard/staticData";
echarts.use([PieChart, TitleComponent, GridComponent, TooltipComponent, LegendComponent, DatasetComponent, CanvasRenderer]);
const pieSeriesSetting = {
    1: {
        radius: '80%',
        x: 50,
        y: 50,
        nextX: 0,
        nextY: 0,
        row: 1,
        col: 1,
        hPow: 1
    },
    2: {
        radius: '60%',
        x: 25,
        y: 50,
        nextX: 50,
        nextY: 0,
        row: 2,
        col: 1,
        hPow: 1
    },
    4: {
        radius: '40%',
        x: 25,
        y: 25,
        nextX: 50,
        nextY: 50,
        row: 2,
        col: 2,
        hPow: 1
    },
    8: {
        radius: '25%',
        x: 20,
        y: 25,
        nextX: 20,
        nextY: 50,
        row: 4,
        col: 2,
        hPow: 1,
    },
    16: {
        radius: '20%',
        x: 13,
        y: 20,
        nextX: 23,
        nextY: 23,
        row: 4,
        col: 4,
        hPow: 1.5
    },
    32: {
        radius: '12%',
        x: 10,
        y: 10,
        nextX: 25,
        nextY: 12,
        row: 4,
        col: 8,
        hPow: 2.5
    },
    64: {
        radius: '10%',
        x: 10,
        y: 6,
        nextX: 25,
        nextY: 6,
        row: 4,
        col: 16,
        hPow: 5
    },
    128: {
        radius: '10%',
        x: 10,
        y: 2,
        nextX: 25,
        nextY: 3,
        row: 4,
        col: 32,
        hPow: 11
    },
    160: {
        radius: '10%',
        x: 10,
        y: 2,
        nextX: 25,
        nextY: 2,
        row: 4,
        col: 40,
        hPow: 16
    },
};
const getNextPowerOfTwo = (length) => {
    if (length <= 1) return 1;
    if (length > 128) return 160;
    let power = 1;
    while (power < length) {
        power *= 2;
    }
    return power;
}
const PieChartBox = React.forwardRef((props, ref) => {
    const {
        grid = { left: "5%", right: "3%" },
        style,
        others = {},
        emphasis = {
            itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
        },
        dark: darkProps
    } = props;
    let option = {
        color: chartColorMap,
        legend: {},
        tooltip: {
            trigger: 'item',
            formatter: function (params) {
                return `${params.seriesName}<br/>${params.marker}${params.name} <strong>${params.value}</strong> (${params.percent}%)`;
            }
        },
        grid,
        title: [],
        dataset: { source: [] },
        series: [],
        ...others,
    };
    const [chartInstance, setChartInstance] = useState(null);
    const [sourceData, setSourceData] = useState(null);
    const [chartHeight, setChartHeight] = useState(Number(style.height.replace('px', '')));
    const [heightPow, setHeightPow] = useState(1);
    const chartRef = useRef(null);
    useImperativeHandle(ref, () => ({
        setSource: (source) => {
            setSourceData(source);
        },
        triggerResize: (width, height) => {
            let currentH = Number(height.replace('px', ''));
            chartRef.current.style.width = width;
            chartRef.current.style.height = `${currentH * heightPow}px`;
            setChartHeight(currentH);
            if (chartInstance) {
                chartInstance.resize(); // 根據容器大小自適應
            }
        }
    }));
    useEffect(
        () => {
            if (!chartInstance || isEmpty(sourceData)) return;
            chartInstance.clear();
            // option.dataset.source = sourceData;
            const titleResult = [];
            const seriesResult = []
            let index = 1;
            const numberKey = getNextPowerOfTwo(sourceData.length - 1);
            const setting = pieSeriesSetting[numberKey];
            while (index < sourceData.length) {
                const data = [];
                let colIndex = 1;
                while (colIndex < sourceData[index].length) {
                    data.push({ name: sourceData[0][colIndex], value: sourceData[index][colIndex] });
                    colIndex++;
                }
                const x = `${setting.x + (((index - 1) % setting.row) * setting.nextX)}%`;
                const y = `${setting.y + (Math.floor((index - 1) / setting.row) * setting.nextY)}%`;
                seriesResult.push({
                    type: 'pie',
                    radius: setting.radius,
                    center: [x, y,],
                    name: sourceData[index][0],
                    data,
                    emphasis,
                });
                titleResult.push({
                    text: sourceData[index][0],
                    left: x,
                    top: y,
                    textAlign: 'center',
                    textVerticalAlign: 'bottom',
                });
                index++;
            }
            option.title = titleResult;
            option.series = seriesResult;
            chartInstance.setOption(option);
            chartRef.current.style.height = `${chartHeight * setting.hPow}px`;
            setHeightPow(setting.hPow);
            if (chartInstance) {
                chartInstance.resize(); // 根據容器大小自適應
            }
        },
        // eslint-disable-next-line
        [sourceData]
    );
    useEffect(
        () => {
            if (!chartRef.current) return;
            const instance = echarts.init(chartRef.current, darkProps ? "dark" : "light");
            setChartInstance(instance);
            return () => {
                instance.dispose();
            };
        },
        // eslint-disable-next-line
        []
    );
    return <Box ref={chartRef} style={style}></Box>;
});
export default PieChartBox;